import type { HttpClient } from '../http/HttpClient.ts';

export interface Comment {
  id: string;
  authorId: string | null;
  authorName: string | null;
  /** The author's agent that wrote it, if one did. */
  agent: { id: string; name: string } | null;
  bodyText: string;
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
}

export interface ActivityEntry {
  id: string;
  actorId: string | null;
  actorName: string | null;
  /** The actor's agent that did it, if one did. */
  agent: { id: string; name: string } | null;
  /** asked/answered: an agent's question to a person, and the answer (or the deadline). */
  verb: 'created' | 'updated' | 'moved_in' | 'asked' | 'answered';
  changes: Record<string, unknown>;
  /** The agent session it was done in. */
  sessionId?: string | null;
  createdAt: string;
}

export interface Watchers {
  watchers: { userId: string; name: string }[];
  watching: boolean;
}

export interface ChecklistEntry {
  id: string;
  text: string;
  done: boolean;
  rank: string;
}

export type LinkKind = 'relates' | 'blocks' | 'duplicates' | 'url';

export interface ItemLink {
  id: string;
  kind: LinkKind;
  direction: 'outgoing' | 'incoming';
  item: { id: string; number: number; title: string; statusId: string } | null;
  url: string | null;
  title: string | null;
}

export interface Attachment {
  id: string;
  filename: string;
  mime: string;
  size: number;
  version: number;
  uploadedBy: string | null;
  uploaderName: string | null;
  createdAt: string;
}

/** One part of an upload: its link takes exactly `size` bytes. */
export interface UploadPart {
  number: number;
  url: string;
  size: number;
}

/** Step 1 of an upload: where to send each part of the bytes, and until when. */
export interface UploadLink {
  id: string;
  method: 'PUT';
  parts: UploadPart[];
  expiresAt: string;
  maxBytes: number;
}

/** Parts sent at once: enough to fill a connection, few enough to leave it usable. */
const PARTS_AT_ONCE = 3;

export interface ReferencingItem {
  id: string;
  number: number;
  title: string;
  type: string;
  statusId: string;
}

/** A place that mentions an item: another item's description, a doc page, or a comment. */
export type ItemReference =
  | { kind: 'linked'; page: { id: string; title: string }; via: 'item' | 'epic'; at: string }
  | { kind: 'page'; page: { id: string; title: string }; at: string }
  | { kind: 'description'; item: ReferencingItem; at: string }
  | { kind: 'comment'; commentId: string; item: ReferencingItem; at: string }
  | { kind: 'comment'; commentId: string; sprint: { id: string; name: string }; at: string }
  | { kind: 'comment'; commentId: string; page: { id: string; title: string }; at: string };

export type NewLink =
  | { kind: 'relates' | 'blocks' | 'duplicates'; targetNumber: number }
  | { kind: 'url'; url: string; title?: string };

const base = (org: string, key: string) =>
  `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}`;

/** Comments, timeline, watching, checklist, links and files of a project's items. */
export class CollabApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  /** The newest 200 comments and changes, or those before `before` (a page's `earlier`). */
  timeline(
    org: string,
    key: string,
    n: number,
    before: string | null,
  ): Promise<{ comments: Comment[]; activity: ActivityEntry[]; earlier: string | null }> {
    return this.#http.get(`${base(org, key)}/items/${n}/timeline`, {
      query: before === null ? {} : { before },
    });
  }

  comment(org: string, key: string, n: number, text: string): Promise<{ comment: Comment }> {
    return this.#http.post(`${base(org, key)}/items/${n}/comments`, { text });
  }

  sprintComments(
    org: string,
    key: string,
    sprintId: string,
    before: string | null,
  ): Promise<{ comments: Comment[]; earlier: string | null }> {
    return this.#http.get(`${base(org, key)}/sprints/${encodeURIComponent(sprintId)}/comments`, {
      query: before === null ? {} : { before },
    });
  }

  commentOnSprint(
    org: string,
    key: string,
    sprintId: string,
    text: string,
  ): Promise<{ comment: Comment }> {
    return this.#http.post(`${base(org, key)}/sprints/${encodeURIComponent(sprintId)}/comments`, {
      text,
    });
  }

  editComment(org: string, key: string, id: string, text: string): Promise<{ comment: Comment }> {
    return this.#http.patch(`${base(org, key)}/comments/${id}`, { text });
  }

  deleteComment(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${base(org, key)}/comments/${id}`);
  }

  watchers(org: string, key: string, n: number): Promise<Watchers> {
    return this.#http.get(`${base(org, key)}/items/${n}/watchers`);
  }

  setWatching(org: string, key: string, n: number, watching: boolean): Promise<Watchers> {
    return this.#http.put(`${base(org, key)}/items/${n}/watch`, { watching });
  }

  sprintWatchers(org: string, key: string, sprintId: string): Promise<Watchers> {
    return this.#http.get(`${base(org, key)}/sprints/${encodeURIComponent(sprintId)}/watchers`);
  }

  setWatchingSprint(
    org: string,
    key: string,
    sprintId: string,
    watching: boolean,
  ): Promise<Watchers> {
    return this.#http.put(`${base(org, key)}/sprints/${encodeURIComponent(sprintId)}/watch`, {
      watching,
    });
  }

  checklist(org: string, key: string, n: number): Promise<{ checklist: ChecklistEntry[] }> {
    return this.#http.get(`${base(org, key)}/items/${n}/checklist`);
  }

  addCheck(org: string, key: string, n: number, text: string): Promise<{ entry: ChecklistEntry }> {
    return this.#http.post(`${base(org, key)}/items/${n}/checklist`, { text });
  }

  updateCheck(
    org: string,
    key: string,
    id: string,
    patch: { text?: string; done?: boolean },
  ): Promise<{ entry: ChecklistEntry }> {
    return this.#http.patch(`${base(org, key)}/checklist/${id}`, patch);
  }

  deleteCheck(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${base(org, key)}/checklist/${id}`);
  }

  reorderChecks(
    org: string,
    key: string,
    n: number,
    ids: string[],
  ): Promise<{ checklist: ChecklistEntry[] }> {
    return this.#http.post(`${base(org, key)}/items/${n}/checklist/reorder`, { ids });
  }

  links(org: string, key: string, n: number): Promise<{ links: ItemLink[] }> {
    return this.#http.get(`${base(org, key)}/items/${n}/links`);
  }

  addLink(org: string, key: string, n: number, link: NewLink): Promise<{ links: ItemLink[] }> {
    return this.#http.post(`${base(org, key)}/items/${n}/links`, link);
  }

  deleteLink(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${base(org, key)}/links/${id}`);
  }

  attachments(org: string, key: string, n: number): Promise<{ attachments: Attachment[] }> {
    return this.#http.get(`${base(org, key)}/items/${n}/attachments`);
  }

  /** A file onto an item. */
  async upload(
    org: string,
    key: string,
    n: number,
    file: File,
  ): Promise<{ attachment: Attachment }> {
    return { attachment: await this.uploadTo(org, key, { item: n }, file) };
  }

  /**
   * Upload in three steps, into the project's files library: for an item (by
   * number) or a doc page (by id), into a Docs folder (null: the top), or as a
   * new version of a library file. A link for this file, its bytes, then the
   * confirmation.
   */
  async uploadTo(
    org: string,
    key: string,
    target:
      | { item: number }
      | { page: string }
      | { library: { folderId: string | null } }
      | { versionOf: string },
    file: File,
  ): Promise<Attachment> {
    const { upload } = await this.#http.post<{ upload: UploadLink }>(`${base(org, key)}/uploads`, {
      name: file.name,
      size: file.size,
      ...target,
    });
    const parts = await this.#sendParts(upload.parts, file);
    const done = await this.#http.post<{ file: Attachment }>(
      `${base(org, key)}/uploads/${upload.id}/complete`,
      { parts },
    );
    return done.file;
  }

  /** Each part's bytes to its link, a few at a time; what storage answered for each. */
  async #sendParts(parts: UploadPart[], file: Blob): Promise<{ number: number; etag: string }[]> {
    const sent: { number: number; etag: string }[] = [];
    let offset = 0;
    const slices = parts.map((part) => {
      const slice = { part, bytes: file.slice(offset, offset + part.size) };
      offset += part.size;
      return slice;
    });
    if (offset !== file.size) throw new Error('The upload links don’t add up to this file');
    const queue = [...slices];
    const worker = async () => {
      for (let next = queue.shift(); next; next = queue.shift())
        sent.push({
          number: next.part.number,
          etag: await this.#http.sendBytes(next.part.url, next.bytes),
        });
    };
    await Promise.all(Array.from({ length: Math.min(PARTS_AT_ONCE, slices.length) }, worker));
    return sent.sort((a, b) => a.number - b.number);
  }

  downloadUrl(org: string, key: string, id: string): string {
    return this.#http.url(`${base(org, key)}/attachments/${encodeURIComponent(id)}/download`);
  }

  /** Take a file off an item (it stays in the library, in Docs). */
  deleteAttachment(org: string, key: string, n: number, id: string): Promise<void> {
    return this.#http.delete(`${base(org, key)}/items/${n}/attachments/${id}`);
  }

  /** Where this item's key is mentioned: other items' descriptions, and comments. */
  references(org: string, key: string, n: number): Promise<{ references: ItemReference[] }> {
    return this.#http.get(`${base(org, key)}/items/${n}/references`);
  }
}
